import { useState, useEffect } from 'react';

/**
 * Hook to manage first-visit intro animation
 * Only shows intro once per session using sessionStorage
 * Respects prefers-reduced-motion
 */
export const useFirstVisitIntro = () => {
  const [showIntro, setShowIntro] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(motionQuery.matches);

    // Listen for changes
    const handleMotionChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };
    motionQuery.addEventListener('change', handleMotionChange);

    // Check if this is the first visit in this session
    const hasSeenIntro = sessionStorage.getItem('funzy-intro-seen');
    
    if (!hasSeenIntro && !motionQuery.matches) {
      setShowIntro(true);
      // Mark as seen
      sessionStorage.setItem('funzy-intro-seen', 'true');
      
      // Complete intro after animation duration (1.8s)
      const timer = setTimeout(() => {
        setIntroComplete(true);
      }, 1800);
      
      return () => {
        clearTimeout(timer);
        motionQuery.removeEventListener('change', handleMotionChange);
      };
    } else {
      // Skip intro for returning visitors or reduced motion
      setIntroComplete(true);
    }

    return () => {
      motionQuery.removeEventListener('change', handleMotionChange);
    };
  }, []);

  return { showIntro, introComplete, prefersReducedMotion };
};
